import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Box, Button, Paper, Typography } from "@mui/material";
import SectionsList from "./SectionsList";
import QuestionsList from "./QuestionsList";
import ImpactGrowsLogo from "images/ImpactGrows.png";
import { submitSurveyResponseSectionWise } from "actions/survey/surveyAction";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiBolerplate from "utils/_helpers/apiBolerplate";
import companyPlaceholder from "../../../../../images/CompanyPlaceholder.png";
import { useDispatch } from "react-redux";
import { set_snack_bar } from "actions/snackbar/snackbar_action";

const getVisibleQuestionIds = (allQuestions, surveyData) => {
  const visibleIds = new Set();

  allQuestions.forEach((q) => visibleIds.add(q._id));

  allQuestions.forEach((parentQ) => {
    parentQ.answers?.forEach((ans) => {
      ans.answerLogic?.forEach((logic) => {
        const linkedId = logic.linked_question_id;

        const parentResponse = surveyData.find(
          (s) => s.question_id === parentQ._id
        );

        const selectedOption = parentResponse?.submitted_answer?.option;

        if (selectedOption !== ans.option) {
          visibleIds.delete(linkedId);
        }
      });
    });
  });

  return visibleIds;
};

const cleanValue = (val) => {
  if (typeof val !== "string") return val;
  const text = val
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, "")
    .trim();
  return text.length > 0 ? text : "";
};

export const isQuestionAnswered = (ans = {}, questionType) => {
  const hasOption = !!cleanValue(ans.option);
  const hasValue = !!cleanValue(ans.value);
  const hasRowData =
    Array.isArray(ans.rowData) &&
    ans.rowData.length > 0 &&
    ans.rowData.every((row) =>
      row.every((cell) => {
        const val = cell?.option ?? cell?.value;
        return val !== undefined && val !== null && val !== "";
      })
    );

  const hasCheckedOption =
    Array.isArray(ans.options) && ans.options.some((opt) => opt.checked);

  const hasDocumentAttached =
    !!ans.document ||
    (Array.isArray(ans.documents) && ans.documents.length > 0);

  const hasValidDate =
    !!ans.startDate &&
    ans.startDate !== "" &&
    (questionType === "DATE_PICKER_SINGLE_DATE" ||
      (!!ans.endDate && ans.endDate !== ""));

  switch (questionType) {
    case "TABLE":
    case "MATRIX":
      return hasRowData;
    case "MULTICHOICE":
      return hasCheckedOption;
    case "DOCUMENT_UPLOAD":
      return hasDocumentAttached;
    case "DATE_PICKER_RANGE_DATE":
    case "DATE_PICKER_SINGLE_DATE":
      return hasValidDate;
    default:
      return hasOption || hasValue;
  }
};

function ContributionSurvey({
  isLoggedIn: loggedInProp,
  entityLogo,
  setLogout,
}) {
  const [isLoggedIn, setIsLoggedIn] = useState(loggedInProp);
  const queryClient = useQueryClient();
  const { survey_id: token } = useParams();
  const [selectedSection, setSelectedSection] = useState({});
  const [surveyData, setSurveyData] = useState([]);
  const dispatch = useDispatch();
  const { data: { result: surveySectionQuestions = [] } = {} } = useQuery({
    queryKey: ["question", selectedSection?._id],
    queryFn: async () =>
      apiBolerplate({
        url: "shared_survey/get_all_questions_for_survey_submission",
        bodyData: {
          section_id: selectedSection?._id,
          access_token: isLoggedIn?.access_token,
          token,
        },
        hideLoader: true,
        hideMsg: true,
      }),
    enabled: !!selectedSection?._id && !!isLoggedIn?.access_token,
    cacheTime: Infinity,
    staleTime: Infinity,
  });

  const [unansweredMandatoryIds, setUnansweredMandatoryIds] = useState([]);

  const { mutateAsync } = useMutation({
    mutationFn: submitSurveyResponseSectionWise,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["question", selectedSection?._id]);
      setSurveyData([]);
    },
    onError: (error) => {
      // Handle error
      console.error("Create failed:", error);
    },
    staleTime: Infinity,
    cacheTime: 0,
  });

  const handleSubmit = async (
    data,
    is_submitted,
    sectionId,
    document,
    documentMetaData
  ) => {
    // const updateDataStructure = transformData(data);
    let response = await mutateAsync({
      token,
      access_token: isLoggedIn?.access_token,
      section_id: sectionId || selectedSection?._id,
      user_id: isLoggedIn?.user_id,
      answers: data,
      is_submitted,
      document,
      documentMetaData,
    });
    return response;
  };

  const handleAllSubmitResponse = async (is_submitted, sectionId = false) => {
    if (is_submitted) {
      const allQuestionQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: ["question"], exact: false });

      const allQuestions = allQuestionQueries.flatMap(
        (query) => query.state.data?.result || []
      );

      const visibleQuestionIds = getVisibleQuestionIds(
        allQuestions,
        surveyData
      );

      const allMandatoryQuestions = allQuestions.filter(
        (q) => q.isMandatory && visibleQuestionIds.has(q._id)
      );

      const unansweredMandatory = allMandatoryQuestions.filter((q) => {
        const localResponse = surveyData.find(
          (s) => s.question_id === q._id || s.question_id === q.id
        );

        const finalAnswer =
          localResponse?.submitted_answer || q.submitted_answer || {};

        return !isQuestionAnswered(finalAnswer, q.questionType);
      });

      if (unansweredMandatory.length > 0) {
        const missingIds = unansweredMandatory.map((q) => q._id);
        setUnansweredMandatoryIds(missingIds);

        dispatch(
          set_snack_bar(
            false,
            `Please answer all mandatory questions before submitting.`
          )
        );
        const firstMissingQuestion = unansweredMandatory[0];
        const firstMissingSectionId = firstMissingQuestion?.section_id;

        if (
          firstMissingSectionId &&
          selectedSection?._id !== firstMissingSectionId
        ) {
          const allSections =
            queryClient
              .getQueryCache()
              .find({ queryKey: ["sections"], exact: false })?.state?.data
              ?.result || [];

          const targetSection = allSections.find(
            (s) => s._id === firstMissingSectionId
          );

          if (targetSection) {
            setSelectedSection(targetSection);
          }
        }

        setTimeout(() => {
          const firstMissing = document.getElementById(missingIds[0]);
          if (firstMissing) {
            firstMissing.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 300);
        return;
      } else {
        setUnansweredMandatoryIds([]);
      }
    }
    const extractDocuments = surveyData
      .flatMap((s) => s.submitted_answer?.document)
      .filter(Boolean);

    const extractDocumentsMetaData = surveyData
      .flatMap((s) => s.submitted_answer?.documentMetaData)
      .filter(Boolean);

    const upadteFormat = surveyData.map(
      ({
        question_id,
        submitted_answer: { document, documentMetaData, ...rest },
      }) => ({
        question_id,
        answer: rest,
      })
    );

    await handleSubmit(
      upadteFormat,
      is_submitted,
      sectionId,
      extractDocuments,
      extractDocumentsMetaData
    );

    if (is_submitted) {
      dispatch(
        set_snack_bar(
          true,
          `The Survey has been Submitted Successfully.`,
          "success"
        )
      );
      const stored = localStorage.getItem("surveyAccess");
      if (stored) {
        const parsed = JSON.parse(atob(stored));
        const updated = {
          ...parsed,
          token: {
            ...parsed.token,
            is_submitted: true,
          },
        };

        localStorage.setItem("surveyAccess", btoa(JSON.stringify(updated)));

        setIsLoggedIn((prev) => ({
          ...prev,
          token: { ...prev.token, is_submitted: true },
          is_submitted: true,
        }));
      }
    }
  };

  const handleQuesAnswer = ({ obj, tableDocuments, realQuestion }) => {
    const sectionId = selectedSection?._id;
    const isAnswered = isQuestionAnswered(obj, realQuestion.questionType);

    setUnansweredMandatoryIds((prev) => {
      if (isAnswered) {
        return prev.filter((id) => id !== realQuestion._id);
      } else if (realQuestion.isMandatory && !prev.includes(realQuestion._id)) {
        return [...prev, realQuestion._id];
      }
      return prev;
    });

    setSurveyData((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item?.question_id === realQuestion?._id
      );

      const newEntry = {
        section_id: sectionId,
        question_id: realQuestion?._id,
        answers: {
          ...obj,
          documents: tableDocuments || [],
        },
        submitted_answer: obj,
      };

      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = { ...newEntry };
        return updated;
      } else {
        return [...prev, newEntry];
      }
    });
  };

  const handleSectionChangeSave = async (section) => {
    if (surveyData.length > 0) {
      await handleAllSubmitResponse(false, selectedSection?._id);
    }
    setSelectedSection(section);
  };

  useEffect(() => {
    if (isLoggedIn?.is_submitted) return;
    let inactivityTimer;
    let debounceTimer;
    let lastActivityTime = 0;

    const INACTIVITY_LIMIT = 30 * 60 * 1000;
    const DEBOUNCE_DELAY = 2000;
    const ACTIVITY_THROTTLE = 2000;

    const startInactivityTimer = () => {
      inactivityTimer = setTimeout(async () => {
        await handleAllSubmitResponse(false);
        localStorage.removeItem("surveyAccess");
        setLogout(false);
        dispatch(set_snack_bar(true, "You were logged out due to inactivity."));
      }, INACTIVITY_LIMIT);
    };

    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastActivityTime < ACTIVITY_THROTTLE) return;
      lastActivityTime = now;
      clearTimeout(debounceTimer);
      clearTimeout(inactivityTimer);
      debounceTimer = setTimeout(() => {
        startInactivityTimer();
      }, DEBOUNCE_DELAY);
    };

    const activityEvents = [
      "mousemove",
      "keydown",
      "click",
      "scroll",
      "touchstart",
    ];
    activityEvents.forEach((event) =>
      window.addEventListener(event, handleUserActivity)
    );
    handleUserActivity();

    return () => {
      clearTimeout(debounceTimer);
      clearTimeout(inactivityTimer);
      activityEvents.forEach((event) =>
        window.removeEventListener(event, handleUserActivity)
      );
    };
  }, [handleAllSubmitResponse, setIsLoggedIn]);
  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <Paper
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1,
          px: 3.5,
        }}
      >
        <img
          alt="logo"
          src={ImpactGrowsLogo}
          style={{ height: 45, objectFit: "cover" }}
        />
        <img
          alt="logo"
          src={entityLogo || companyPlaceholder}
          style={{
            height: 45,
            width: 45,
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
      </Paper>

      <Box sx={{ px: 4, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="h3" fontWeight={600}>
            {isLoggedIn?.survey_name}
          </Typography>
          {isLoggedIn?.is_submitted && (
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: "#BFE2CD",
                color: "#154328",
                pointerEvents: "none",
              }}
            >
              Survey Submitted
            </Button>
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "10px",
            mb: 1.5,
            display: "flex",
            height: "calc(100vh - 200px)",
            overflowY: "auto",
            boxShadow: (theme) =>
              `3px -3px 8px -3px ${theme.palette.primary.main}25, 
       -3px 3px 8px -3px ${theme.palette.primary.main}25,
       0px 6px 16px -4px ${theme.palette.primary.main}40`,
          }}
        >
          <SectionsList
            isLoggedIn={isLoggedIn}
            selectedSection={selectedSection}
            handleSectionChangeSave={handleSectionChangeSave}
          />
          <QuestionsList
            selectedSection={selectedSection}
            surveyData={surveyData}
            handleAllSubmitResponse={handleAllSubmitResponse}
            handleQuesAnswer={handleQuesAnswer}
            surveySectionQuestions={surveySectionQuestions}
            disable={isLoggedIn?.is_submitted}
            unansweredMandatoryIds={unansweredMandatoryIds}
            handleSectionChangeSave={handleSectionChangeSave}
          />
        </Paper>
      </Box>
    </Box>
  );
}

export default ContributionSurvey;
